import { Dropdown } from "#root/components/dropdown";
import { useDropdown, useEventListener } from "#root/utility/hooks";
import type { Transform } from "#root/utility/transforms";
import { Invalid, Neutral, Valid, type Validator } from "#root/utility/validators";
import type { ReactNode } from "react";
import { type SelectHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFieldSet } from "./fieldset";

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  inboundTransform?: Transform<unknown, string | undefined>;
  outboundTransform?: Transform<string | undefined, unknown>;
  validator?: Validator;
  required?: boolean;
  dirty?: boolean;
  defaultValue?: string;
};

export function SelectField({
                         inboundTransform = (value: unknown) => (value ? String(value) : undefined),
                         outboundTransform = (value: string | undefined) => value,
                         validator,
                         required,
                         dirty: startDirty = false,
  defaultValue = "",
                         className = "",
                         children,
                       }: SelectProps) {
  const fieldSet = useFieldSet();

  const [value, setValue] = useState<string | undefined>(fieldSet?.field?.value == null ? defaultValue : inboundTransform(fieldSet?.field?.value));
  const [valueName, setValueName] = useState<string | null>(null);
  const [dirty, setDirty] = useState<boolean>(startDirty);
  const [open, setOpen] = useState<boolean>(false);

  const [dropdown, toggleDropdown] = useDropdown();

  const { validity, hint } = useMemo(() => {
    if (value == null || value.length === 0) {
      if (required) {
        return { validity: Invalid, hint: "Required" };
      }

      return { validity: Neutral, hint: "Optional" };
    }

    if (validator) {
      return validator(value) || { validity: Valid, hint: "Valid" };
    }

    return { validity: Valid, hint: "Valid" };
  }, [value, required, validator]);

  useEffect(() => {
    fieldSet?.mutate(object => ({
      ...object,
      dirty,
      validity,
      hint,
      value: outboundTransform(value),
      focused: open,
    }));
  }, [fieldSet, dirty, validity, hint, value, outboundTransform, open]);

  switch (validity) {
    case Valid: {
      className += "valid";

      break;
    }

    case Invalid: {
      className += "invalid";

      break;
    }

    case Neutral: {
      className += "neutral";

      break;
    }
    // No default
  }


  const handleChildClick = useCallback(evnt => {
    evnt.preventDefault();
    evnt.stopPropagation();

    if (!evnt.target) {
      return;
    }

    if (!evnt.target.dataset) {
      return;
    }

    if (!evnt.target.dataset.value) {
      return;
    }

    setValue(evnt.target.dataset.value);
    setValueName(evnt.target.dataset.textContent);
    setOpen(false);
    setDirty(true);
  }, []);

  const handleBodyClick = useCallback(() => {
    setOpen(false);
  }, []);

  useEventListener(document.body, "click", handleBodyClick);

  const itemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!itemRef.current) {
      return;
    }

    for (const element of itemRef.current.querySelectorAll("button")) {
      if (element.dataset.value === value?.toString()) {
        setValueName(element.textContent);
      }
    }
  }, [value]);

  const borderColor = validity === Invalid ? "border-red-400" : validity === Valid ? "border-green-400" : "border-[#ddd]";

  return (
    <div className={`relative w-full border ${borderColor} rounded bg-white transition-colors ${open ? "border-[#39f] ring-2 ring-[#39f]/20" : ""}`}>
      <Dropdown id="select" label={valueName || "Select"} open={dropdown === "select"} onToggle={toggleDropdown}
                className="w-full px-3 py-2 text-base text-[#333]">
        <div ref={itemRef} className="items" onClick={handleChildClick}>
          {children}
        </div>
      </Dropdown>
    </div>
  );
}

export function OptionField({ value, children }: { value: number | string | null; children?: ReactNode }) {
  return (
    <button data-value={value} className="block">
      {children}
    </button>
  );
}

// Option = Option;
// BooleanSelect = Boolean;
